// src/ConsumerApp/Screens/Account/components/DeliveryGroupCard.js

import React, { useState } from 'react';
import Key from '../../../../assets/icons/Key.svg';
import CaretDown from '../../../../assets/icons/CaretDown.svg';
import CaretUp from '../../../../assets/icons/CaretUpBlack.svg';
import Lightning from '../../../../assets/icons/Lightning.svg';
import CheckCircleGreen from '../../../../assets/icons/CheckCircleGreen.svg';
import CheckCircleGray from '../../../../assets/icons/CheckCircleGray.svg';
import Truck from '../../../../assets/icons/Truck.svg';
import GrayTruck from '../../../../assets/icons/GrayTruck.svg';
import Package from '../../../../assets/icons/Package.svg';
import ShootingStar from '../../../../assets/icons/ShootingStar.svg';
import CurrencyInr from '../../../../assets/icons/CurrencyInr.svg';
import Prohibit from '../../../../assets/icons/Prohibit.svg';
import ToteIcon from '../../../../assets/icons/tote.svg';

const IMAGE_BASE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

const INSTANT_STEPS = [
  { id: 'CONFIRMED', icon: CheckCircleGreen, iconPending: CheckCircleGray, title: 'Shipment Created', activeDesc: 'Order confirmed and waiting for schedule.', upcomingDesc: 'Waiting for delivery schedule.' },
  { id: 'PACKING', icon: Package, iconPending: Package, title: 'Items Packed', activeDesc: 'Your items are packed and ready.', upcomingDesc: 'Ready for delivery.' },
  { id: 'ON_THE_WAY', icon: Truck, iconPending: GrayTruck, title: 'Out for delivery', activeDesc: 'Your order is on the way.', upcomingDesc: 'Out for delivery.' },
  { id: 'DELIVERED', icon: CheckCircleGreen, iconPending: CheckCircleGray, title: 'Delivered successfully', activeDesc: 'Order has been handed to you successfully.', upcomingDesc: 'Delivered successfully.' }
];

const RFQ_STEPS = [
  { id: 'SUBMITTED', icon: CheckCircleGreen, iconPending: CheckCircleGray, title: 'RFQ submitted', activeDesc: 'Request sent successfully.', upcomingDesc: 'Request sent successfully.' },
  { id: 'QUOTE_RECEIVED', icon: ShootingStar, iconPending: ShootingStar, title: 'Best quotation received', activeDesc: 'Pay now to lock the special price.', upcomingDesc: 'Best quote confirmed.' },
  { id: 'PAYMENT', icon: CurrencyInr, iconPending: CurrencyInr, title: 'Payment completed', activeDesc: 'Payment completed successfully.', upcomingDesc: 'Complete payment to place order.' },
  { id: 'PACKING', icon: Package, iconPending: Package, title: 'Packing', activeDesc: 'Your order is being packed.', upcomingDesc: 'Order starts packing.' },
  { id: 'ON_THE_WAY', icon: Truck, iconPending: Truck, title: 'On the way', activeDesc: 'Reaching you today.', upcomingDesc: 'Truck dispatch.' },
  { id: 'DELIVERED', icon: CheckCircleGreen, iconPending: CheckCircleGray, title: 'Delivered successfully', activeDesc: 'Order has been handed to you successfully.', upcomingDesc: 'Order has been handed to you successfully.' }
];

const mapStatusToKey = (statusStr) => {
  if (!statusStr) return 'CONFIRMED';
  const s = statusStr.toLowerCase();
  if (s.includes('quote')) return 'QUOTE_RECEIVED';
  if (s.includes('waiting') || s.includes('confirmed')) return 'CONFIRMED';
  if (s.includes('ready') || s.includes('packing')) return 'PACKING';
  if (s.includes('out') || s.includes('way') || s.includes('delivering')) return 'ON_THE_WAY';
  if (s.includes('delivered')) return 'DELIVERED';
  return 'CONFIRMED';
};

function OrderItemImage({ src, alt }) {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return (
      <div style={styles.itemThumbFallback}>
        <img src={ToteIcon} alt="fallback" style={{ width: 20, height: 20, opacity: 0.4 }} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      style={styles.itemThumbImg}
      onError={() => setHasError(true)}
    />
  );
}

export default function DeliveryGroupCard({ group, isRfq, orderStatus, orderData }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const isCancelled = orderStatus === 'Cancelled';

  const baseSteps = isRfq ? RFQ_STEPS : INSTANT_STEPS;
  const displaySteps = isCancelled
    ? [...baseSteps, { id: 'CANCELLED', title: 'Canceled', activeDesc: 'Order closed via cancellation.', upcomingDesc: 'Order closed via cancellation.' }]
    : baseSteps;

  const groupStatus = isCancelled ? 'CANCELLED' : mapStatusToKey(group.delivery_group_status);
  const currentStepIndex = Math.max(0, displaySteps.findIndex((s) => s.id === groupStatus));
  const activeStep = displaySteps[currentStepIndex];

  const totalItemsCount = group.delivery_categories?.reduce(
    (total, cat) => total + (cat.items?.length || 0),
    0
  ) || 0;

  const showEta = !group.order_delivered_otp && !isCancelled && groupStatus !== 'DELIVERED';
  const etaText = orderData?.delivery_eta
    ? `${orderData.delivery_eta.minMinutes} - ${orderData.delivery_eta.maxMinutes} mins`
    : orderData?.delivery_slot?.label || group.estimated_delivery_time || '30 - 90 mins';

  return (
    <div style={styles.cardContainer}>
      {/* OTP Display Card */}
      {group?.order_delivered_otp && group.delivery_group_status !== 'Delivered' && (
        <div style={styles.otpCard}>
          <div style={styles.otpHeaderRow}>
            <div style={styles.otpIconBox}>
              <img src={Key} alt="Key" style={{ width: 18, height: 18 }} />
            </div>
            <span style={styles.otpTitle}>
              Share this OTP with the delivery partner after checking your items.
            </span>
          </div>
          <div style={styles.otpValueBox}>
            <span style={styles.otpValueLabel}>Delivery OTP</span>
            <span style={styles.otpValue}>{group.order_delivered_otp}</span>
          </div>
        </div>
      )}

      {/* Tracking Card */}
      <div style={styles.trackingCard}>
        <div style={styles.trackingHeader} onClick={() => setIsExpanded(!isExpanded)}>
          <div>
            {showEta ? (
              <div style={styles.etaRow}>
                <img src={Lightning} alt="Lightning" style={{ width: 16, height: 16 }} />
                <span style={styles.etaText}>Arriving in {etaText}</span>
              </div>
            ) : (
              <span style={styles.trackingTitle}>
                {isCancelled ? 'Order Cancelled' : activeStep?.title || 'Delivery Status'}
              </span>
            )}
          </div>
          <div style={{ cursor: 'pointer' }}>
            <img src={isExpanded ? CaretDown : CaretUp} alt="toggle" style={{ width: 12, height: 12 }} />
          </div>
        </div>

        {isExpanded && (
          <div style={styles.stepsContainer}>
            {displaySteps.map((stepConfig, index) => {
              const isCancelStep = stepConfig.id === 'CANCELLED';
              const isSkipped = isCancelled && index > 0 && !isCancelStep;
              const isCompleted = !isCancelled && index < currentStepIndex;
              const isActive = index === currentStepIndex;
              const isUpcoming = !isCancelled && index > currentStepIndex;
              const isLast = index === displaySteps.length - 1;

              const StepIcon = isCancelStep
                ? Prohibit
                : (isCompleted || isActive || (isCancelled && index === 0))
                ? CheckCircleGreen
                : (stepConfig.iconPending || CheckCircleGray);

              return (
                <div key={stepConfig.id} style={styles.stepRow}>
                  <div style={styles.stepIconCol}>
                    <div
                      style={{
                        ...styles.stepIconCircle,
                        backgroundColor: isCancelStep
                          ? '#FFF0ED'
                          : isCompleted || isActive
                          ? '#E8F5EE'
                          : '#F5F3EF',
                      }}
                    >
                      <img src={StepIcon} alt="step" style={{ width: 14, height: 14 }} />
                    </div>
                    {!isLast && (
                      <div style={styles.dotsCol}>
                        {[...Array(4)].map((_, i) => (
                          <div
                            key={i}
                            style={{
                              ...styles.dot,
                              backgroundColor: isCompleted && !isCancelled ? '#1E8449' : '#CBD5E1',
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={styles.stepTextCol}>
                    <span
                      style={{
                        ...styles.stepTitle,
                        color: isCancelStep ? '#E95A1E' : isUpcoming || isSkipped ? '#707B8C' : '#2B2A29',
                        textDecoration: isSkipped ? 'line-through' : 'none',
                      }}
                    >
                      {stepConfig.title}
                    </span>
                    <span style={styles.stepDesc}>
                      {isActive || isCancelStep ? stepConfig.activeDesc : stepConfig.upcomingDesc}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Items Box */}
      <div style={styles.itemsBox}>
        <span style={styles.itemsCountLabel}>
          {totalItemsCount === 1 ? 'Item' : 'Items'} · {totalItemsCount}
        </span>

        {group.delivery_categories?.map((cat) => (
          <div key={cat.product_category_id || Math.random()}>
            {cat.items?.map((item, idx) => {
              const imgUrl = item.item_image ? `${IMAGE_BASE_URL}${item.item_image}` : null;
              return (
                <div key={item.item_id || idx} style={styles.itemLine}>
                  <div style={styles.itemThumbBox}>
                    <OrderItemImage src={imgUrl} alt={item.item_name} />
                  </div>
                  <div style={styles.itemMetaCol}>
                    <div style={styles.itemTitleRow}>
                      <span style={styles.itemTitleText}>{item.item_name}</span>
                      <span style={styles.itemPriceText}>
                        ₹{Number(item.total || item.price || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <span style={styles.itemQtyText}>
                      Qty: {item.quantity} {item.UOM || ''}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  cardContainer: { display: 'flex', flexDirection: 'column', gap: 12 },
  otpCard: { backgroundColor: '#FFFDF5', border: '1px solid #FDE68A', borderRadius: 12, padding: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  otpHeaderRow: { display: 'flex', alignItems: 'center', gap: 10, flex: 1 },
  otpIconBox: { width: 32, height: 32, borderRadius: 16, backgroundColor: '#FEF3C7', display: 'flex', justifyContent: 'center', alignItems: 'center' },
  otpTitle: { fontSize: 12, color: '#92400E', lineHeight: '16px' },
  otpValueBox: { textAlign: 'right', marginLeft: 16 },
  otpValueLabel: { fontSize: 10, color: '#707B8C', display: 'block' },
  otpValue: { fontSize: 18, fontWeight: '700', color: '#1E8449' },
  trackingCard: { backgroundColor: '#FFFFFF', borderRadius: 12, border: '1px solid #CBD5E1', padding: 16 },
  trackingHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' },
  trackingTitle: { fontSize: 14, fontWeight: '700', color: '#2B2A29' },
  etaRow: { display: 'flex', alignItems: 'center', gap: 6 },
  etaText: { fontSize: 13, fontWeight: '600', color: '#1E8449' },
  stepsContainer: { marginTop: 16, display: 'flex', flexDirection: 'column' },
  stepRow: { display: 'flex', gap: 12, minHeight: 48 },
  stepIconCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: 24 },
  stepIconCircle: { width: 22, height: 22, borderRadius: 11, display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1 },
  dotsCol: { display: 'flex', flexDirection: 'column', gap: 3, margin: '3px 0' },
  dot: { width: 3, height: 3, borderRadius: 1.5 },
  stepTextCol: { flex: 1, paddingBottom: 14 },
  stepTitle: { fontSize: 13, fontWeight: '600', display: 'block' },
  stepDesc: { fontSize: 11, color: '#707B8C' },
  itemsBox: { backgroundColor: '#FFFFFF', borderRadius: 12, border: '1px solid #CBD5E1', padding: 16 },
  itemsCountLabel: { fontSize: 13, color: '#2B2A29', fontWeight: '600', display: 'block', marginBottom: 12 },
  itemLine: { display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 12, marginBottom: 12, borderBottom: '1px solid #F1F5F9' },
  itemThumbBox: { width: 44, height: 44, borderRadius: 6, backgroundColor: '#F8F6F2', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' },
  itemThumbImg: { width: '100%', height: '100%', objectFit: 'contain' },
  itemThumbFallback: { width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#FFFFFF' },
  itemMetaCol: { flex: 1 },
  itemTitleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  itemTitleText: { fontSize: 13, fontWeight: '600', color: '#2B2A29' },
  itemPriceText: { fontSize: 13, fontWeight: '700', color: '#2B2A29' },
  itemQtyText: { fontSize: 11, color: '#707B8C' },
};